'use client';

import { useCallback, useMemo, useState } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { Languages, Play } from 'lucide-react';

import {
  DISPLAY_LABEL,
  FROM_LABEL,
  SUPPORTED,
  LANGUAGE_LABELS,
  LangDisplay,
  Locale,
} from './constants';
import { PATH_TRANSLATIONS } from '@/lib/pathTranslations';
import { usePathTranslation } from '@/app/[lang]/providers/PathTranslationProvider';
import { resolveLocalizedPath } from '@/services/navigation.service';

export function LanguageSwitcher() {
  const [open, setOpen] = useState(false);
  const pathname = usePathname() || '/';
  const router = useRouter();

  const currentLocale: Locale = useMemo(() => {
    const seg = pathname.split('/')[1]?.toLowerCase();
    return SUPPORTED.includes(seg as Locale) ? (seg as Locale) : Locale.ID; // fallback to enum
  }, [pathname]);

  const currentLangLabel = DISPLAY_LABEL[currentLocale];

  const { translations: pathTranslations } = usePathTranslation();

  const handleLangChange = useCallback(
    async (label: LangDisplay) => {
      const nextLocale = FROM_LABEL[label];
      if (nextLocale === currentLocale) {
        setOpen(false);
        return;
      }
      // Remove locale prefix to get the slug
      const slug = pathname.replace(/^\/(en|id)(\/|$)/, '');

      const go = (target: string) => {
        setOpen(false);
        router.push(target.replace(/\/+/g, '/'));
      };

      // 1. Use navigation_path_translations from the current page's API response (most accurate)
      if (pathTranslations?.[nextLocale]) {
        go(`/${nextLocale}${pathTranslations[nextLocale]}`);
        return;
      }

      // 2. Resolve through the navigation tree so every segment is localized
      const navPath = await resolveLocalizedPath(slug, currentLocale, nextLocale);
      if (navPath) {
        go(`/${nextLocale}${navPath}`);
        return;
      }

      // 3. Fetch this page's data for the target locale. The API synthesizes
      //    navigationPathTranslations from the template record's localized slug, so
      //    this resolves pages that are not bound to a navigation node.
      try {
        const res = await fetch(`/api/dynamic-content/${slug}?lang=${nextLocale}`);
        if (res.ok) {
          const json = await res.json();
          if (json?.success && json?.data) {
            const translatedPath: string | undefined =
              json.data.navigationPathTranslations?.[nextLocale];
            if (translatedPath) {
              go(`/${nextLocale}${translatedPath}`);
              return;
            }
          }
        }
      } catch {
        // ignore, fall through to static map
      }

      // 4. Fall back to static PATH_TRANSLATIONS with prefix matching
      const translations = PATH_TRANSLATIONS[currentLocale] || {};
      let mapped = translations[`/${slug}`];
      if (!mapped) {
        let bestPrefixLen = 0;
        let bestTranslation = '';
        for (const [key, val] of Object.entries(translations)) {
          const s = `/${slug}`;
          if ((s === key || s.startsWith(key + '/')) && key.length > bestPrefixLen) {
            bestPrefixLen = key.length;
            bestTranslation = val;
          }
        }
        mapped = bestPrefixLen > 0 ? bestTranslation + (`/${slug}`).slice(bestPrefixLen) : `/${slug}`;
      }

      go(`/${nextLocale}${mapped}`);
    },
    [pathname, router, currentLocale, pathTranslations]
  );

  return (
    <div className="relative shrink-0">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1 rounded-md px-2 py-2 font-semibold text-[#124673] transition-colors hover:bg-[#124673] hover:text-white md:gap-2 md:px-3"
      >
        <Languages size={18} aria-hidden />
        {currentLangLabel}
        <Play
          size={12}
          fill="currentColor"
          className={`rotate-90 transition-transform ${open ? 'rotate-[270deg]' : ''}`}
          aria-hidden
        />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute left-0 mt-2 w-28 rounded-md bg-white shadow-md"
          onMouseLeave={() => setOpen(false)}
        >
          <ul className="text-gray-700">
            {LANGUAGE_LABELS.map((label, idx) => (
              <li key={label}>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => handleLangChange(label)}
                  className={[
                    'w-full px-4 py-2 text-left hover:bg-[#124673] hover:text-white',
                    idx === 0 ? 'rounded-t-md' : idx === LANGUAGE_LABELS.length - 1 ? 'rounded-b-md' : '',
                    label === currentLangLabel ? 'font-bold' : '',
                  ].join(' ')}
                >
                  {label}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
